import { __decorate, __metadata } from "tslib";
import { CacheActivePromise } from '@1inch-community/core/decorators';
import { lazyAppContext } from '@1inch-community/core/lazy';
import { createTransport, http, webSocket } from 'viem';
const defaultRPCConfig = {
    batchSize: 100,
};
export class WebFallbackTransportController {
    constructor(chain) {
        this.chain = chain;
        this.httpTransportSet = new Set();
        this.wsTransportSet = new Set();
        this.context = lazyAppContext('WebFallbackTransportController');
    }
    async init(context) {
        this.context.set(context);
        await this.initRPC();
    }
    async benchMartTransport() {
        await Promise.all([
            ...this.httpTransportSet.values().toArray().map(benchMartTransport),
            ...this.wsTransportSet.values().toArray().map(benchMartTransport),
        ]);
    }
    createTransport() {
        return () => createTransport({
            key: 'fallback',
            name: 'fallback',
            type: 'fallback',
            request: ({ method, params }) => this.onRequest(method, params),
        }, {});
    }
    async onRequest(method, params) {
        return await this.fetch(method, params);
    }
    async fetch(method, params) {
        const { http, ws } = this.getBestTransport();
        try {
            const result = await Promise.any([
                http
                    ? request(http, method, params)
                    : Promise.reject(new Error('No http transports available')),
                ws ? request(ws, method, params) : Promise.reject(new Error('No ws transports available')),
            ]);
            return result;
        }
        catch (error) {
            if (error instanceof AggregateError) {
                return;
            }
            throw error;
        }
    }
    async initRPC() {
        const { getRPC } = await import('../transport-map');
        const { http, ws } = getRPC(this.chain.id);
        const httpTransportHolderList = buildTransportHolderList(this.chain, http);
        const wsTransportHolderList = buildTransportHolderList(this.chain, ws);
        httpTransportHolderList.forEach((item) => this.httpTransportSet.add(item));
        wsTransportHolderList.forEach((item) => this.wsTransportSet.add(item));
    }
    getBestTransport() {
        const bestTransport = {
            http: undefined,
            ws: undefined,
        };
        this.httpTransportSet.forEach((transport) => (bestTransport.http = compareTransportHolder(transport, bestTransport.http)));
        this.wsTransportSet.forEach((transport) => (bestTransport.ws = compareTransportHolder(transport, bestTransport.ws)));
        return bestTransport;
    }
}
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], WebFallbackTransportController.prototype, "benchMartTransport", null);
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, Object]),
    __metadata("design:returntype", Promise)
], WebFallbackTransportController.prototype, "onRequest", null);
function compareTransportHolder(th1, th2) {
    if (!th1.success) {
        return th2;
    }
    if (th2 === undefined) {
        return th1;
    }
    if (th2.countErrors > th1.countErrors) {
        return th1;
    }
    if (th2.time > th1.time) {
        return th1;
    }
    return th2;
}
async function request(transportHolder, method, params) {
    const { transport } = transportHolder;
    return await transport
        .request({ method, params }, { dedupe: true, retryCount: 0 })
        .catch((err) => {
        transportHolder.countErrors++;
        return Promise.reject(err);
    });
}
async function benchMartTransport(transportHolder) {
    const { rpc, config, chain, type, transport } = transportHolder;
    const _transport = type === 'ws'
        ? transport
        : buildTransport(chain, buildTransportFactory(rpc, { ...config, batchSize: false }));
    const start = Date.now();
    let end;
    try {
        transportHolder.success = await _transport
            .request({ method: 'net_listening' })
            .catch(() => _transport.request({ method: 'web3_clientVersion' }))
            .catch(() => _transport.request({ method: 'eth_blockNumber' }))
            .catch(() => _transport.request({ method: 'net_version' }))
            .catch(() => _transport.request({ method: 'eth_syncing' }))
            .then((result) => {
            return !!result;
        });
    }
    catch {
        transportHolder.success = false;
        // console.groupCollapsed('RPC bench mart error', rpc)
        // console.error('chain: ', chain.id, chain.name)
        // console.error('rpc: ', rpc)
        // console.error(error)
        // console.groupEnd()
    }
    finally {
        end = Date.now();
        transportHolder.time = end - start;
    }
}
function buildTransportHolderList(chain, rpcList) {
    return rpcList.map((rpcAndConfig) => {
        const rpc = typeof rpcAndConfig === 'string' ? rpcAndConfig : rpcAndConfig.rpc;
        const config = typeof rpcAndConfig === 'string' ? defaultRPCConfig : rpcAndConfig.config;
        return {
            chain,
            rpc,
            config,
            countErrors: 0,
            type: rpc.startsWith('https://') ? 'http' : 'ws',
            transport: buildTransport(chain, buildTransportFactory(rpc, config)),
            success: true,
            time: Infinity,
        };
    });
}
function buildTransportFactory(rpc, config) {
    if (rpc.startsWith('https://')) {
        return http(rpc, {
            retryCount: 0,
            timeout: 3_000,
            batch: config.batchSize === false
                ? undefined
                : {
                    batchSize: config.batchSize,
                    wait: 300,
                },
        });
    }
    if (rpc.startsWith('wss://')) {
        return webSocket(rpc, {
            retryCount: 1,
            retryDelay: 1_000,
            timeout: 10_000,
            reconnect: false,
        });
    }
    throw new Error(`Unsupported RPC URL ${rpc}`);
}
function buildTransport(chain, transportFactory) {
    return transportFactory({
        chain,
        retryCount: 0,
        timeout: 2_000,
        pollingInterval: 0,
    });
}
//# sourceMappingURL=web-fallback-transport.controller.js.map